import { motion } from "framer-motion";
import {
  ChatCircleText,
  ClipboardText,
  MagnifyingGlass,
  HandCoins,
  CheckCircle,
  TreeStructure,
} from "@phosphor-icons/react";

const steps = [
  {
    icon: ChatCircleText,
    title: "Initial Consultation",
    desc: "We listen to your objectives, constraints and target markets in a confidential discovery call.",
  },
  {
    icon: ClipboardText,
    title: "Business Requirement Analysis",
    desc: "We structure a detailed brief covering product specs, volumes, certifications and commercial terms.",
  },
  {
    icon: MagnifyingGlass,
    title: "Supplier / Partner Identification",
    desc: "We screen our network and source qualified counterparts that match your verified requirements.",
  },
  {
    icon: HandCoins,
    title: "Negotiation & Matchmaking",
    desc: "We bridge both parties through structured introductions, term-sheet discussions and clarifications.",
  },
  {
    icon: CheckCircle,
    title: "Deal Facilitation",
    desc: "We support contract finalisation, payment terms, shipping coordination and milestone tracking.",
  },
  {
    icon: TreeStructure,
    title: "Long-Term Partnership Support",
    desc: "Beyond the first transaction we stay engaged to safeguard the relationship and unlock future deals.",
  },
];

export default function Process() {
  return (
    <section
      id="process"
      data-testid="process-section"
      className="py-24 md:py-32 bg-altin-bg"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-3xl">
          <div className="flex items-center gap-3 mb-6">
            <span className="gold-divider" />
            <span className="overline">Our Business Process</span>
          </div>
          <h2 className="font-serif-display text-4xl md:text-5xl leading-[1.05] text-altin-ink tracking-tight">
            How we{" "}
            <span className="italic text-altin-gold">work</span>.
          </h2>
          <p className="mt-6 text-lg text-altin-muted font-light max-w-2xl leading-relaxed">
            A disciplined six-stage methodology refined across hundreds of
            international commercial engagements.
          </p>
        </div>

        <div className="mt-20 relative">
          <div className="hidden lg:block absolute left-0 right-0 top-[68px] h-px bg-altin-border" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-10 lg:gap-6">
            {steps.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.div
                  key={s.title}
                  data-testid={`process-step-${i}`}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.55, delay: i * 0.1 }}
                  className="relative"
                >
                  <div className="relative z-10 w-[136px] mx-auto lg:mx-0">
                    <div className="w-[136px] h-[136px] grid place-items-center bg-altin-bg border border-altin-border group hover:border-altin-gold transition-colors">
                      <div className="text-center">
                        <div className="text-[10px] tracking-[0.3em] uppercase text-altin-gold mb-2">
                          {String(i + 1).padStart(2, "0")}
                        </div>
                        <Icon
                          size={32}
                          weight="light"
                          className="text-altin-ink mx-auto"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="mt-6 text-center lg:text-left">
                    <h3 className="font-serif-display text-xl text-altin-ink tracking-tight">
                      {s.title}
                    </h3>
                    <p className="mt-2 text-sm text-altin-muted leading-relaxed">
                      {s.desc}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
